/**
 * HITL-1747. Reports main-thread stalls to New Relic.
 *
 * Guest audio is drained on the main thread, so a long synchronous task shows
 * up for the agent as a frozen console AND an audio gap at the same moment.
 * Nothing measured that before: the audio watchdog stamps frames on the same
 * thread, so a stall looked identical to a network gap.
 *
 * Logs only — it changes no behaviour. Each stall is attributed to the item
 * most recently built (see menuItemBuildCache), and logging is throttled so a
 * run of stalls costs one log line plus a suppressed count.
 */
import logger from './logger';
import { getLastItemBuild } from './menuItemBuildCache';

export const MAIN_THREAD_STALL_MS = 200;
const LOG_THROTTLE_MS = 5000;

let observer: PerformanceObserver | null = null;
let lastLoggedAt = 0;
let suppressed = 0;

export const handleLongTask = (durationMs: number, now = Date.now()) => {
  if (durationMs < MAIN_THREAD_STALL_MS) return;
  if (now - lastLoggedAt < LOG_THROTTLE_MS) {
    suppressed += 1;
    return;
  }
  const lastBuild = getLastItemBuild();
  logger.warn({
    message: `Main thread stalled for ${Math.round(durationMs)}ms`,
    stallMs: Math.round(durationMs),
    stallsSuppressedSinceLastLog: suppressed,
    lastBuiltItem: lastBuild?.itemName,
    lastBuiltItemReused: lastBuild?.reused,
    lastBuiltItemAgoMs: lastBuild ? now - lastBuild.at : undefined,
  });
  lastLoggedAt = now;
  suppressed = 0;
};

export const startMainThreadStallMonitor = () => {
  if (observer || typeof PerformanceObserver === 'undefined') return;
  if (!PerformanceObserver.supportedEntryTypes?.includes('longtask')) return;
  // Telemetry must never take the app down: a browser that rejects the
  // observer simply goes unmonitored.
  try {
    observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => handleLongTask(entry.duration));
    });
    observer.observe({ type: 'longtask', buffered: false });
  } catch {
    observer = null;
  }
};

export const resetMainThreadStallMonitorForTests = () => {
  observer?.disconnect();
  observer = null;
  lastLoggedAt = 0;
  suppressed = 0;
};
